import React, { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle, AlertTriangle, Info, Tag, Megaphone } from 'lucide-react';

const API_URL = (() => { const envUrl = process.env.REACT_APP_BACKEND_URL; if (!envUrl || envUrl === '') return window.location.origin; try { const envHost = new URL(envUrl).hostname; const currentHost = window.location.hostname; if (currentHost === 'localhost' || currentHost === '127.0.0.1' || envHost === currentHost || currentHost.includes('preview.emergentagent.com')) { return envUrl; } return window.location.origin; } catch (e) { return window.location.origin; } })();

// Enhanced notice styles - more bold and noticeable
const noticeStyles = {
  info: {
    bg: 'bg-blue-600/30 border-blue-400 border-2',
    text: 'text-blue-100',
    icon: Info,
    iconColor: 'text-blue-300'
  },
  success: {
    bg: 'bg-green-600/30 border-green-400 border-2',
    text: 'text-green-100',
    icon: CheckCircle,
    iconColor: 'text-green-300'
  },
  warning: {
    bg: 'bg-amber-600/40 border-amber-400 border-2',
    text: 'text-amber-100',
    icon: AlertTriangle,
    iconColor: 'text-amber-300'
  },
  error: {
    bg: 'bg-red-600/40 border-red-400 border-2',
    text: 'text-red-100',
    icon: AlertCircle,
    iconColor: 'text-red-300'
  },
  promo: {
    bg: 'bg-gradient-to-r from-purple-600/40 to-pink-600/40 border-purple-400 border-2',
    text: 'text-purple-100',
    icon: Tag,
    iconColor: 'text-purple-300'
  },
  announcement: {
    bg: 'bg-gradient-to-r from-cyan-600/40 to-blue-600/40 border-cyan-400 border-2',
    text: 'text-cyan-100',
    icon: Megaphone,
    iconColor: 'text-cyan-300'
  }
};

const WebsiteNotices = ({ location = 'all' }) => {
  const [notices, setNotices] = useState([]);
  const [dismissedNotices, setDismissedNotices] = useState([]);

  useEffect(() => {
    fetchNotices();
    // Load dismissed notices from localStorage
    const dismissed = JSON.parse(localStorage.getItem('dismissed_notices') || '[]');
    setDismissedNotices(dismissed);
  }, []);

  const fetchNotices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notices?location=${location}`);
      if (response.ok) {
        const data = await response.json();
        setNotices(data.notices || []);
      }
    } catch (error) {
      console.error('Failed to fetch notices:', error);
    }
  };

  const dismissNotice = (noticeId) => {
    const newDismissed = [...dismissedNotices, noticeId];
    setDismissedNotices(newDismissed);
    localStorage.setItem('dismissed_notices', JSON.stringify(newDismissed));
  };

  // Filter out dismissed notices and show only for this location
  const visibleNotices = notices.filter(notice => 
    !dismissedNotices.includes(notice.id) && 
    (notice.location === 'all' || notice.location === location)
  );

  if (visibleNotices.length === 0) return null;

  return (
    <div className="space-y-3">
      {visibleNotices.map(notice => {
        const style = noticeStyles[notice.notice_type] || noticeStyles.info;
        const IconComponent = style.icon;
        
        return (
          <div 
            key={notice.id}
            className={`${style.bg} border rounded-xl px-5 py-4 flex items-start gap-4 shadow-lg animate-pulse-slow`}
            data-testid={`website-notice-${notice.id}`}
            style={{ animation: 'none' }}
          >
            <div className={`w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0`}>
              <IconComponent className={`w-6 h-6 ${style.iconColor || style.text}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`font-bold text-lg ${style.text}`}>{notice.title}</p>
              <p className="text-white/90 text-base mt-1 font-medium">{notice.message}</p>
              {notice.link_url && (
                <a 
                  href={notice.link_url}
                  className={`inline-flex items-center gap-1 mt-3 text-sm font-semibold ${style.text} hover:underline bg-white/10 px-3 py-1.5 rounded-full`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {notice.link_text || 'Learn More'} →
                </a>
              )}
            </div>
            {notice.show_close_button && (
              <button
                onClick={() => dismissNotice(notice.id)}
                className="text-slate-400 hover:text-white transition-colors"
                aria-label="Dismiss notice"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Sticky Banner Notice - For header/top of page
export const StickyBannerNotice = () => {
  const [notices, setNotices] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchNotices();
    const dismissedBanner = sessionStorage.getItem('dismissed_banner');
    if (dismissedBanner) setDismissed(true);
  }, []);

  useEffect(() => {
    if (notices.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % notices.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [notices.length]);

  const fetchNotices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notices?location=header_banner`);
      if (response.ok) {
        const data = await response.json();
        setNotices(data.notices || []);
      }
    } catch (error) {
      console.error('Failed to fetch banner notices:', error);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('dismissed_banner', 'true');
  };

  if (dismissed || notices.length === 0) return null;

  const notice = notices[currentIndex];
  const style = noticeStyles[notice.notice_type] || noticeStyles.promo;

  return (
    <div className={`${style.bg} border-b ${style.text.replace('text-', 'border-')}/30`}>
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-3 text-sm">
        <span className="font-semibold">{notice.title}</span>
        <span className="hidden sm:inline text-slate-300">, </span>
        <span className="hidden sm:inline text-slate-300">{notice.message}</span>
        {notice.link_url && (
          <a 
            href={notice.link_url}
            className={`${style.text} hover:underline font-medium`}
          >
            {notice.link_text || 'Learn More'}
          </a>
        )}
        <button
          onClick={handleDismiss}
          className="ml-4 text-slate-400 hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Page-specific notice component
export const PageNotice = ({ location }) => {
  const [notices, setNotices] = useState([]);
  const [dismissedNotices, setDismissedNotices] = useState([]);

  useEffect(() => {
    fetchNotices();
    const dismissed = JSON.parse(localStorage.getItem('dismissed_page_notices') || '[]');
    setDismissedNotices(dismissed);
  }, [location]);

  const fetchNotices = async () => {
    try {
      // Fetch notices for this specific location OR 'all' locations
      const response = await fetch(`${API_URL}/api/notices`);
      if (response.ok) {
        const data = await response.json();
        // Filter notices for this page location or 'all'
        const pageNotices = (data.notices || []).filter(n => 
          n.is_active && (n.location === location || n.location === 'all')
        );
        setNotices(pageNotices);
      }
    } catch (error) {
      console.error('Failed to fetch page notices:', error);
    }
  };

  const dismissNotice = (noticeId) => {
    const newDismissed = [...dismissedNotices, noticeId];
    setDismissedNotices(newDismissed);
    localStorage.setItem('dismissed_page_notices', JSON.stringify(newDismissed));
  };

  const visibleNotices = notices.filter(n => !dismissedNotices.includes(n.id));

  if (visibleNotices.length === 0) return null;

  return (
    <div className="space-y-3 mb-6">
      {visibleNotices.map(notice => {
        const style = noticeStyles[notice.notice_type] || noticeStyles.info;
        const Icon = style.icon;
        
        return (
          <div 
            key={notice.id}
            className={`${style.bg} border rounded-xl p-4 flex items-start gap-3`}
          >
            <Icon className={`w-5 h-5 ${style.text} flex-shrink-0 mt-0.5`} />
            <div className="flex-1">
              {notice.title && (
                <h4 className={`font-semibold ${style.text} mb-1`}>{notice.title}</h4>
              )}
              <p className="text-slate-300 text-sm">{notice.message}</p>
              {notice.link_url && notice.link_text && (
                <a 
                  href={notice.link_url}
                  className={`inline-flex items-center gap-1 mt-2 text-sm ${style.text} hover:underline`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {notice.link_text} →
                </a>
              )}
            </div>
            {notice.show_close_button && (
              <button
                onClick={() => dismissNotice(notice.id)}
                className="text-slate-400 hover:text-white p-1"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default WebsiteNotices;
